import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { useMutation, gql, useQuery } from '@apollo/client';
import type { Business, BusinessOffer } from '../../types/business';

// Définir les mutations
const CREATE_BUSINESS_OFFER = gql`
  mutation CreateBusinessOffer($input: BusinessOfferInput!) {
    createBusinessOffer(input: $input) {
      id
      title
      description
      validUntil
      discount
      minimumParticipants
      status
    }
  }
`;

const UPDATE_BUSINESS_OFFER = gql`
  mutation UpdateBusinessOffer($id: Int!, $input: BusinessOfferInput!) {
    updateBusinessOffer(id: $id, input: $input) {
      id
      title
      description
      validUntil
      discount
      minimumParticipants
      status
    }
  }
`;

// Ajouter la mutation de suppression
const DELETE_BUSINESS_OFFER = gql`
  mutation DeleteBusinessOffer($id: Int!) {
    deleteBusinessOffer(id: $id) {
      id
    }
  }
`;

// Requête pour récupérer les packages de l'agence connectée
const GET_AGENCY_PACKAGES = gql`
  query GetAgencyPackagesForOffer($agencyName: String!) {
    agencyPackages(agencyName: $agencyName) {
      id
      title
      type
    }
  }
`;

// Définir le schéma de validation
const offerSchema = z.object({
  title: z.string().min(2, 'Le titre est requis'),
  description: z.string().min(2, 'La description est requise'),
  validUntil: z.string().min(1, 'La date de validité est requise'),
  discount: z.number().min(0).max(100, 'La réduction doit être entre 0 et 100'),
  minimumParticipants: z.number().min(1, 'Le nombre minimum de participants est requis'),
  packageId: z.number().min(1, 'Le package est requis')
});

type OfferFormData = z.infer<typeof offerSchema>;

interface BusinessOfferFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  business: Business;
  selectedOffer?: BusinessOffer | null;
  title?: string;
}

interface Package {
  id: number;
  title: string;
  type: 'B2B' | 'B2C';
}

const BusinessOfferFormModal: React.FC<BusinessOfferFormModalProps> = ({ 
  isOpen, 
  onClose, 
  business,
  selectedOffer,
  title = "Nouvelle offre"
}) => {
  const [createOrUpdateOffer] = useMutation(
    selectedOffer ? UPDATE_BUSINESS_OFFER : CREATE_BUSINESS_OFFER
  );

  const [deleteOffer] = useMutation(DELETE_BUSINESS_OFFER, {
    onCompleted: () => {
      onClose();
    },
    onError: (error) => {
      console.error('Erreur lors de la suppression:', error);
      // Afficher un message d'erreur à l'utilisateur
      alert('Impossible de supprimer cette offre car elle est liée à des clients. Veuillez d\'abord supprimer les clients associés.');
    }
  });

  const handleDelete = async () => {
    if (selectedOffer && window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      try {
        await deleteOffer({
          variables: { id: selectedOffer.id }
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  // Récupérer les informations de l'agence depuis le localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const agencyName = user?.agencyName || user?.agency?.name;
  
  // Requête pour récupérer les packages de l'agence
  const { data: packagesData, loading, error } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName: agencyName || '' },
    skip: !agencyName
  });
  
  // Filtrer les packages de type B2B (offres partenaires)
  const packages = (packagesData?.agencyPackages || [])
    .filter((pkg: Package) => pkg.type === 'B2B');
  

  // Fonction pour formater la date en toute sécurité
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return '';
    }
  };

  const { register, handleSubmit, formState: { errors } } = useForm<OfferFormData>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      title: selectedOffer?.title || '',
      description: selectedOffer?.description || '',
      validUntil: formatDate(selectedOffer?.validUntil),
      discount: selectedOffer?.discount || 0,
      minimumParticipants: selectedOffer?.minimumParticipants || 1,
      packageId: selectedOffer?.packageId || 0
    }
  });

  const onSubmit = async (data: OfferFormData) => {
    try {
      // Formater la date en ISO-8601 complète
      const formattedData = {
        ...data,
        validUntil: new Date(data.validUntil).toISOString(),
        businessId: business.id
      };

      const variables = selectedOffer 
        ? { id: selectedOffer.id, input: formattedData }
        : { input: formattedData };
        
      await createOrUpdateOffer({
        variables
      });
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full p-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Titre *</label>
            <input
              type="text"
              {...register('title')}
              className="w-full px-3 py-2 border rounded-md"
            />
            {errors.title && <p className="text-red-600 text-sm">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description *</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border rounded-md"
            />
            {errors.description && <p className="text-red-600 text-sm">{errors.description.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Package *</label>
            <select
              {...register('packageId', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Sélectionner un package</option>
              {!agencyName ? (
                <option value="" disabled>Session invalide - reconnectez-vous</option>
              ) : loading ? (
                <option value="" disabled>Chargement des packages...</option>
              ) : error ? (
                <option value="" disabled>Erreur de chargement des packages</option>
              ) : packages.length === 0 ? (
                <option value="" disabled>Aucun package B2B disponible pour votre agence</option>
              ) : (
                packages.map((pkg: Package) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.title}
                  </option>
                ))
              )}
            </select>
            {errors.packageId && <p className="text-red-600 text-sm">{errors.packageId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date de validité *</label>
            <input
              type="date"
              {...register('validUntil')}
              className="w-full px-3 py-2 border rounded-md"
            />
            {errors.validUntil && <p className="text-red-600 text-sm">{errors.validUntil.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Réduction (%) *</label>
            <input
              type="number"
              {...register('discount', { valueAsNumber: true })}
              min="0"
              max="100"
              className="w-full px-3 py-2 border rounded-md"
            />
            {errors.discount && <p className="text-red-600 text-sm">{errors.discount.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Nombre minimum de participants *</label>
            <input
              type="number"
              {...register('minimumParticipants', { valueAsNumber: true })}
              min="1"
              className="w-full px-3 py-2 border rounded-md"
            />
            {errors.minimumParticipants && <p className="text-red-600 text-sm">{errors.minimumParticipants.message}</p>}
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            {selectedOffer && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer l'offre
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {selectedOffer ? 'Modifier l\'offre' : 'Créer l\'offre'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessOfferFormModal;